import React, { useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-flexbox-grid'
import styled from 'styled-components'
import cloneDeep from 'lodash/cloneDeep'

import { Text, Checkbox, Select, Form, Number as NumberInput } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import printerLocations from 'gql/query/appConfig/printerLocations'
import globalStyles from 'style/app.module.scss'
import { useQuery } from 'components/graphql'
import RemoveButton from 'components/shared/icons/Close'

const PrinterLocation = ({ formData, parameters, updateItem }) => {
	const [t] = useTranslation()

	// get 'printer location data' (in uncomfortable form)
	const { data: filthyPrinterLocationData } = useQuery(printerLocations, { fetchPolicy: 'cache-first' })
	// make 'printer location data' as array (and filter it by dbIndex, from small to big)
	const fetchedLocations = filthyPrinterLocationData?.getAppConfig?.cashRegisterGlobal?.printerLocations
	const printerLocationData = Array.isArray(fetchedLocations) ? [...fetchedLocations].sort(({ dbIndex: first_idx }, { dbIndex: second_idx }) => first_idx - second_idx) : null

	// information (array) about printers: isActive, ip, printer
	const printerInfo = formData?.parameters?.printer?.locations

	const listPrinters = []

	// preparing pretty "listPrinters" array
	printerLocationData && printerInfo && printerInfo.forEach((printer) => {
		let printLocation = null
		printerLocationData.forEach(dataLocation => {
			if(dataLocation.dbIndex === printer.position) {
				printLocation = dataLocation.value
			}
		})
		printLocation && listPrinters.push({
			location: printLocation,
			...printer
		})
	})

	const kdsSerialNumCheck = (serialNum) => {
		if(!serialNum || typeof serialNum !== 'string') {
			return false
		}
		return serialNum.length === 13
	}
	const getPrinterFields = (printerData) => {
		const { ip, ipDeviceLabel } = printerData || {}
		if(!ip && !ipDeviceLabel) {
			return {}
		}
		const out = { ip }
		const [type, kds] = (ipDeviceLabel?.trim() || '').split(';')
		if(kds && kdsSerialNumCheck(kds)) {
			out.kds = kds
		}
		if(!type) {
			out.type = 'epson'
			return out
		}
		switch(type) {
			case 'sunmi': out.type = 'sunmi'; break
			case 'sunmi_cloud': out.type = 'sunmi_cloud'; break
			default: {
				if(kdsSerialNumCheck(type)) {
					out.type = 'kds'
				}
			}
		}

		return out
	}
	const initPrinters = listPrinters.reduce((outObj, item) => {
		outObj[item.position] = getPrinterFields(item)
		return outObj
	}, {})
	const [printers, setPrinters] = useState(initPrinters)

	const printerModelsOptions = useMemo(() => ([
		{ label: 'Epson M30', value: 'epson' },
		{ label: 'SUNMI_CLOUD by IP', value: 'sunmi' },
		{ label: 'SUNMI_CLOUD by Server', value: 'sunmi_cloud' },
		{ label: 'Kitchen Display (KDS)', value: 'kds' }
	]), [])

	const handleSubmit = vals => {
		const printerVals = cloneDeep(vals)
		// prepare data for printers
		const locations = printerVals?.parameters?.printer?.locations
		if (locations && Object.keys(locations).length !== 0) {
			let newLocationArr = []
			locations.forEach((item) => {
				if(item) {
					const { position, ...rest } = item
					newLocationArr.push({ value: { ...rest }, position })
				}
			})
			printerVals.parameters.printer.locations = newLocationArr
		}

		updateItem(printerVals, {
			optimisticResponse: optimistic => {
				const newOptimistic = cloneDeep(optimistic)
				return newOptimistic
			}
		})
	}

	const handlePrinterFieldsChange = ({ name, position, value }) => {
		setPrinters(prev => ({
			...prev,
			[position]: {
				...prev[position],
				[name]: value
			}
		}))
	}
	const printersTypeRef = useRef({})

	return(
		!listPrinters.length
			? t('common.strings.table_no_data')
			: <Form
				smartSubmit
				initialValues={formData}
				onSubmit={handleSubmit}
				className={globalStyles['flex-cols']}
			>
				{({ formApi, formState }) => (
					<>
						<Checkbox field='parameters.printer.isKitchenPrinterActive' label={t('options_section.fields.isKitchenPrinterActive')}/>
						<Checkbox field='parameters.printer.isPrintKitchenBillAfterCompletionOnly' label={t('options_section.fields.isPrintKitchenBillAfterCompletionOnly')}/>
						<Checkbox field='parameters.printer.isPrintKitchenBillForTableOnly' label={t('options_section.fields.isPrintKitchenBillForTableOnly')}/>
						<h4>{t('options_section.strings.kitchen_locations')}</h4>
						<Divider height={20}/>
						{listPrinters && listPrinters.map((printer) => (
							<HighlightedRow key={printer.position}>
								<Col
									xs={2}
									style={{ alignSelf: 'center' }}
								>
									<h4
										style={{
											fontWeight: '500'
										}}
									>{printer.location}</h4>
								</Col>
								<Col xs={2} style={{ alignSelf: 'center' }}>
									<SCheckbox
										field={`parameters.printer.locations[${printer.position}].isActive`}
										label={t('options_section.strings.active')}
										onChange={
											(e) => {
												const way = `parameters.printer.locations[${printer.position}].isDirectConnected`
												formApi.setValue(way, e.target.checked)
												if(!e.target.checked) {
													formApi.setError(`parameters.printer.locations[${printer.position}]`, null)
												}
											}
										}
									/>
									<SCheckbox
										field={`parameters.printer.locations[${printer.position}].copyForWaiter`}
										label={t('options_section.strings.copy_for_waiter')}
									/>
									<SCheckbox
										field={`parameters.printer.locations[${printer.position}].tableShiftReceipt`}
										label={t('options_section.strings.table_shift_receipt')}
									/>
								</Col>
								<Col xs={4}>
									{/* hidden info */}
									<div
										style={{ display: 'none' }}
									>
										<Text
											field={`parameters.printer.locations[${printer.position}].position`}
											label='Location position'
											readOnly
											hidden
										/>
										<Checkbox
											field={`parameters.printer.locations[${printer.position}].isDirectConnected`}
											label='isDirectConnected'
											readOnly
										/>
									</div>
									<Select
										noForm
										value={printers[printer.position]?.type}
										onChange={v => {
											const { value } = v || {}
											if(value) {
												setPrinters(prev => ({ ...prev, [printer.position]: { type: value } }))
												printersTypeRef.current = { ...printersTypeRef.current, [printer.position]: value }
												const deviceValue = value === 'epson' || value === 'kds' ? null : value
												formApi.setValue(`parameters.printer.locations[${printer.position}].ip`, null)
												formApi.setValue(`parameters.printer.locations[${printer.position}].ipDeviceLabel`, deviceValue)
												if(value === 'kds') {
													formApi.setError(`parameters.printer.locations[${printer.position}].ip`, null)
												}
											}
										}}
										options={printerModelsOptions}
										error={formState.errors?.parameters?.printer?.locations?.[printer.position] && !printers[printer.position]?.type ? t('validators.required') : null}
										label={t('options_section.strings.printer')}
									/>
									{(!printers[printer.position]?.type || printers[printer.position]?.type === 'kds') ? null : printers[printer.position]?.kds
										?
										<SWithRemove>
											<Text
												noForm
												label={t('options_section.strings.kitchen_printer_serial_number')}
												value={typeof printers[printer.position]?.kds === 'string' ? printers[printer.position]?.kds : ''}
												onChange={e => {
													const valuePath = `parameters.printer.locations[${printer.position}].ipDeviceLabel`
													let newDeviceLabelValue = formApi.getValue(valuePath)
													let [type, kds] = (newDeviceLabelValue?.trim() || '').split(';')
													kds = e.target.value
													newDeviceLabelValue = [type, kds].join(';')
													formApi.setValue(valuePath, newDeviceLabelValue)
													handlePrinterFieldsChange({ name: 'kds', position: printer.position, value: e.target.value || true })
												}}
												onBlur={() => formApi.submitForm()}
											/>
											<RemoveButton onClick={() => {
												handlePrinterFieldsChange({ name: 'kds', position: printer.position, value: false })
												const valuePath = `parameters.printer.locations[${printer.position}].ipDeviceLabel`
												const newDeviceLabelValue = formApi.getValue(valuePath)
												const [type, kds] = (newDeviceLabelValue?.trim() || '').split(';')
												if(kds) {
													formApi.setValue(valuePath, type)
												}
												formApi.submitForm()
											}} />
										</SWithRemove>
										: <SAddButton
											onClick={() => handlePrinterFieldsChange({ name: 'kds', position: printer.position, value: true })}
											text={<><span>+</span>{t('options_section.strings.add_kitchen_display')}</>}
											status='cancelation'
										/>
									}
								</Col>
								<Col xs={4} style={{ ...!printers[printer.position]?.type && { display: 'none' } }}>
									<div>
										<Text
											field={`parameters.printer.locations[${printer.position}].ipDeviceLabel`}
											label={t('options_section.strings.serial_number')}
											validate={(v, vals) => {
												if(!vals.parameters?.printer?.locations?.[printer.position].isActive) {
													return null
												}
												return printersTypeRef.current[printer.position] === 'kds' ? v ? null : t('validators.required') : null
											}}
											validateOnChange
											validateOnMount={false}
											hidden={printers[printer.position]?.type !== 'kds'}
											readOnly={printers[printer.position]?.type !== 'kds'}
										/>
									</div>
									<div>
										<Text
											field={`parameters.printer.locations[${printer.position}].ip`}
											label={
												printers[printer.position]?.type === 'sunmi'
													? t('options_section.strings.ip_address') : t('options_section.strings.serial_number')
											}
											validate={(v, vals) => {
												if(!vals.parameters?.printer?.locations?.[printer.position].isActive) {
													return null
												}
												return printersTypeRef.current[printer.position] !== 'kds' ? v ? null : t('validators.required') : null
											}}
											validateOnChange
											hidden={printers[printer.position]?.type === 'kds'}
											readOnly={printers[printer.position]?.type === 'kds'}
										/>
									</div>
									{/*<Text field={`parameters.printer.locations[${printer.position}].ipDeviceLabel`} label={t('options_section.strings.device_id')} />*/}
								</Col>
							</HighlightedRow>
						))}
						<Checkbox
							field='parameters.printer.separateKitchenBill'
							label={t('options_section.strings.separate_kitchen_bill')} />
						<NumberInput
							field='parameters.printer.topMargin'
							label={t('options_section.strings.top_margin')}
							shrink
						/>
					</>
				)}
			</Form>
	)
}

const HighlightedRow = styled(Row)`
	margin-bottom: 0.5rem;
	padding: 20px;
	background-color: #f4f3f384;
	border-radius: 5px;
`
const SCheckbox = styled(Checkbox)`
	label {
		align-items: center;
	}
`
const SAddButton = styled(Button)`
	display: inline-flex;
  align-items: center;
  min-width: 0;
	height: 36px;
	border-radius: 36px;
  box-shadow: 0px 1px 2px rgb(90 90 90 / 12%);
	font-size: 12px;
	text-transform: none;
	span {
		margin-right: 10px;
		font-size: 18px;
	}
`
const SWithRemove = styled.div`
	position: relative;
	.icon-close {
		position: absolute;
		right: 10px;
		bottom: 9px;
		z-index: 100;
	}
`

export default PrinterLocation
